import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Copy, Camera } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/Layout";
import { AccountSelect } from "@/components/AccountSelect";
import { EmptyAccounts } from "@/components/EmptyAccounts";
import { TradeDialog } from "@/components/TradeDialog";
import { ScreenshotsDialog } from "@/components/ScreenshotsDialog";
import { useAccounts } from "@/hooks/useAccounts";
import { api, errMsg } from "@/lib/api";
import { SESSIONS, fmtPnl, pnlClass, fmtUtc } from "@/lib/trading";

const SessionCell = ({ trade, s }) => {
  if (!trade.sessions.includes(s.key)) return <td className="px-3 py-2 text-center text-slate-700 font-mono">·</td>;
  return (
    <td className="px-3 py-2 text-center" style={{ background: `${s.color}10` }}>
      <span className={`font-mono text-xs font-semibold ${pnlClass(trade.pnl)}`}>{fmtPnl(trade.pnl)}</span>
    </td>
  );
};

const sumBy = (rows, key) => rows.filter((t) => t.sessions.includes(key)).reduce((a, t) => a + t.pnl, 0);

const DayGroup = ({ date, rows, accName, onEdit, onDelete, onShots }) => {
  const total = rows.reduce((a, t) => a + t.pnl, 0);
  return (
    <tbody>
      {rows.map((t) => (
        <tr key={t.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors" data-testid={`trade-row-${t.id}`}>
          <td className="px-3 py-2 font-mono text-xs text-slate-400 whitespace-nowrap">{fmtUtc(t.entry_time).slice(11)}</td>
          <td className="px-3 py-2 font-mono font-semibold">{t.symbol}{t.copied_from && <Copy className="inline h-3 w-3 ml-1 text-cyan-400" />}</td>
          <td className="px-3 py-2"><span className={`text-xs font-mono uppercase ${t.side === "buy" ? "text-emerald-400" : "text-red-400"}`}>{t.side}</span></td>
          <td className="px-3 py-2 font-mono text-xs">{t.lots}</td>
          <td className="px-3 py-2 font-mono text-xs text-slate-400 whitespace-nowrap">{t.entry_price} → {t.exit_price ?? "—"}</td>
          <td className="px-3 py-2 text-xs text-slate-400 whitespace-nowrap">{accName(t.account_id)}</td>
          {SESSIONS.map((s) => <SessionCell key={s.key} trade={t} s={s} />)}
          <td className={`px-3 py-2 text-right font-mono font-semibold ${pnlClass(t.pnl)}`}>{fmtPnl(t.pnl)}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">
            <button onClick={() => onShots(t)} data-testid={`screenshots-trade-${t.id}`} className={`p-1 inline-flex items-center gap-0.5 ${t.screenshots?.length ? "text-cyan-400" : "text-slate-500"} hover:text-cyan-300`}>
              <Camera className="h-3.5 w-3.5" />{t.screenshots?.length > 0 && <span className="font-mono text-[10px]">{t.screenshots.length}</span>}
            </button>
            <button onClick={() => onEdit(t)} data-testid={`edit-trade-${t.id}`} className="p-1 text-slate-500 hover:text-slate-200"><Pencil className="h-3.5 w-3.5" /></button>
            <button onClick={() => onDelete(t)} data-testid={`delete-trade-${t.id}`} className="p-1 text-slate-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
          </td>
        </tr>
      ))}
      <tr className="bg-slate-900/80 border-b-2 border-slate-700" data-testid={`daily-total-${date}`}>
        <td colSpan={6} className="px-3 py-2 font-mono text-xs text-slate-300">{date} · Daily total ({rows.length} trades)</td>
        {SESSIONS.map((s) => <td key={s.key} className={`px-3 py-2 text-center font-mono text-xs ${pnlClass(sumBy(rows, s.key))}`}>{fmtPnl(sumBy(rows, s.key))}</td>)}
        <td className={`px-3 py-2 text-right font-mono font-bold ${pnlClass(total)}`} data-testid={`daily-total-value-${date}`}>{fmtPnl(total)}</td>
        <td />
      </tr>
    </tbody>
  );
};

export default function Journal() {
  const { accounts, loaded } = useAccounts();
  const [acc, setAcc] = useState("all");
  const [session, setSession] = useState("all");
  const [trades, setTrades] = useState([]);
  const [dialog, setDialog] = useState({ open: false, trade: null });
  const [shotsTrade, setShotsTrade] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (acc !== "all") params.account_id = acc;
    if (session !== "all") params.session = session;
    api.get("/trades", { params }).then((r) => setTrades(r.data));
  }, [acc, session]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const m = {};
    trades.forEach((t) => (m[t.trade_date] ||= []).push(t));
    return Object.entries(m);
  }, [trades]);

  const accName = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.broker} · ${a.account_number}` : "—"; };
  const del = async (t) => {
    if (!window.confirm(`Delete ${t.symbol} trade?${t.copied_from ? "" : " Copied slave trades will also be removed."}`)) return;
    try { await api.delete(`/trades/${t.id}`); toast.success("Trade deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (loaded && !accounts.length) return (<><PageHeader title="Journal" /><EmptyAccounts /></>);

  return (
    <>
      <PageHeader title="Trade Journal" subtitle="Each entry is placed in the session column(s) open at entry time (UTC). Overlaps count in both.">
        <AccountSelect accounts={accounts} value={acc} onChange={setAcc} allowAll testId="journal-account-filter" />
        <Button onClick={() => setDialog({ open: true, trade: null })} data-testid="add-trade-button" className="bg-emerald-600 hover:bg-emerald-500 text-white"><Plus className="h-4 w-4 mr-1" />Log trade</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="session-filter-bar">
        {[{ key: "all", label: "All sessions" }, ...SESSIONS].map((s) => (
          <button key={s.key} onClick={() => setSession(s.key)} data-testid={`session-filter-${s.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-mono border transition-colors ${session === s.key ? (s.badge || "bg-emerald-500/10 text-emerald-400 border-emerald-500/30") : "border-slate-800 text-slate-400 hover:text-slate-200"}`}>
            {s.label}{s.hours ? ` · ${s.hours}` : ""}
          </button>
        ))}
      </div>
      <div className="panel overflow-x-auto rise" data-testid="journal-table">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="border-b border-slate-800 text-xs font-mono uppercase tracking-wider text-slate-500">
              {["Time", "Symbol", "Side", "Lots", "Entry → Exit", "Account"].map((h) => <th key={h} className="px-3 py-3 text-left font-medium">{h}</th>)}
              {SESSIONS.map((s) => <th key={s.key} className="px-3 py-3 text-center font-medium" style={{ color: s.color }}>{s.label}</th>)}
              <th className="px-3 py-3 text-right font-medium">Net P&L</th><th />
            </tr>
          </thead>
          {groups.map(([date, rows]) => <DayGroup key={date} date={date} rows={rows} accName={accName} onEdit={(t) => setDialog({ open: true, trade: t })} onDelete={del} onShots={setShotsTrade} />)}
        </table>
        {!groups.length && <p className="p-10 text-center text-slate-500 text-sm" data-testid="journal-empty">No trades yet. Log your first entry.</p>}
      </div>
      <ScreenshotsDialog trade={shotsTrade} onClose={() => setShotsTrade(null)} onChanged={load} />
      <TradeDialog open={dialog.open} trade={dialog.trade} onOpenChange={(o) => setDialog({ ...dialog, open: o })} accounts={accounts} defaultAccount={acc !== "all" ? acc : undefined} onSaved={load} />
    </>
  );
}
